import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CSpinner, CAlert, CButton } from '@coreui/react';
import { axiosApiCallWithToken } from '../../../axios/customInstance';

const AuthGuard = ({ children, requireAdmin = false }) => {
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    useEffect(() => {
        checkAuthentication();
    }, []);

    const checkAuthentication = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                redirectToLogin();
                return;
            }

            // Check authentication status with backend
            const response = await axiosApiCallWithToken.get('/profileChangeRequest/auth-check');
            
            if (response.status === 200) {
                const { authenticated, isAdmin } = response.data;
                
                setAuthenticated(authenticated);
                setIsAdmin(isAdmin);
                
                // If admin is required but user is not admin
                if (requireAdmin && !isAdmin) {
                    console.log("Access denied: Admin privileges required but user is not admin");
                    setError(t('administrative_privileges_required'));
                    setLoading(false);
                    return;
                }
                
                setLoading(false);
            } else {
                redirectToLogin();
            }
        } catch (error) {
            console.error('Authentication check failed:', error);
            if (error.response && error.response.status === 401) {
                redirectToLogin();
            } else {
                setError(t('authentication_check_failed'));
                setLoading(false);
            }
        }
    };

    const redirectToLogin = () => {
        // Store the current URL to redirect back after login
        const currentPath = location.pathname + location.search;
        localStorage.setItem('redirectAfterLogin', currentPath);
        
        // Clear any existing auth tokens
        localStorage.removeItem('auth_token');
        localStorage.removeItem('name');
        localStorage.removeItem('surname');
        localStorage.removeItem('authority');
        localStorage.removeItem('doctor_page');
        localStorage.removeItem('admin_page');
        localStorage.removeItem('patient_id');
        localStorage.removeItem('staff_id');
        localStorage.removeItem('patient_data');
        
        navigate('/login');
    };

    const handleRedirectButton = () => {
        // Check if user is authenticated and determine appropriate redirect
        if (authenticated) {
            const authority = localStorage.getItem('authority');
            if (authority === 'ADMIN') {
                navigate('/dashboard_admin');
            } else if (authority === 'DOCTOR') {
                navigate('/patients');
            } else {
                // Fallback to login if authority is unclear
                navigate('/login');
            }
        } else {
            // User is not authenticated, redirect to login
            navigate('/login');
        }
    };

    const getRedirectButtonText = () => {
        if (authenticated) {
            const authority = localStorage.getItem('authority');
            if (authority === 'ADMIN') {
                return t('go_to_dashboard');
            } else if (authority === 'DOCTOR') {
                return t('go_to_patients');
            } else {
                return t('go_to_login');
            }
        } else {
            return t('go_to_login');
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="text-center">
                    <CSpinner color="primary" size="lg" />
                    <p className="mt-3">{t('verifying_authentication')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-5">
                <CAlert color="danger">
                    <h4>{t('access_denied')}</h4>
                    <p>{error}</p>
                    <div className="d-flex gap-2 flex-wrap">
                        <CButton 
                            color="primary"
                            onClick={handleRedirectButton}
                        >
                            {getRedirectButtonText()}
                        </CButton>
                    </div>
                </CAlert>
            </div>
        );
    }

    if (!authenticated) {
        return null; // Will redirect to login
    }

    return children;
};

export default AuthGuard;